import { formatDistance } from 'date-fns';
import { DEFAULT_CHARACTER_IDS } from 'dynamo-daos/build/models/jobs/characterUploadRecord';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';

import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import {
  CHARACTER_TYPE_IN_USE_SOURCES,
  trackCharacterTypeInUse,
} from '@cartwhl/tracking/featureTracking';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Loading from '@cartwhl/ui/atoms/Loading';
import ToggleGroup from '@cartwhl/ui/atoms/ToggleGroup';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';
import SubPanel from '@cartwhl/ui/layout/SubPanel';
import AnimationPreview from '@cartwhl/ui/molecules/AnimationPreview';

import {
  characterPickerStore,
  navigationStore,
  noticesStore,
  projectStore,
  sceneSettingsStore,
  uiStore,
} from 'stores';

import { CUSTOM_TAB, DEFAULT_TAB } from '../constants';
import { byName, freeChars, latest, uploadedChars } from '../utils';

export default function CharactersSubpanel({
  id,
  elementToTrack,
}: {
  id: string;
  elementToTrack: HTMLDivElement | HTMLButtonElement | null;
}) {
  const { characters } = useSnapshot(characterPickerStore.state);
  const {
    characterSettings: { charUploadID },
  } = useSnapshot(sceneSettingsStore.state);
  const { subPanelSectionKey, subPanelKey } = useSnapshot(uiStore.state);

  const freeCharacters = useMemo(() => {
    if (!characters) return [];
    const prioritized = [];
    const rest = [];
    const remaining = characters.filter(freeChars);
    // Pick those matching the priority names in order
    const PRIORITY_IDs = [
      DEFAULT_CHARACTER_IDS.AXEL,
      DEFAULT_CHARACTER_IDS.MANNY,
      DEFAULT_CHARACTER_IDS.QUINN,
      DEFAULT_CHARACTER_IDS.UEFN_MANNEQUIN,
    ];
    for (const characterID of PRIORITY_IDs) {
      const idx = remaining.findIndex((c) => c.PK === characterID);
      if (idx !== -1) {
        prioritized.push(remaining.splice(idx, 1)[0]);
      }
    }
    rest.push(...remaining.sort(byName));
    return [...prioritized, ...rest];
  }, [characters]);
  const uploadedCharacters =
    characters
      ?.filter(uploadedChars)
      .filter((character) => character.uploadStatus !== 'FAILED')
      .sort(latest) || [];
  const charactersToDisplay =
    subPanelSectionKey === CUSTOM_TAB ? uploadedCharacters : freeCharacters;

  const isProcessing = (status: string) => {
    return [
      'PENDING',
      'AUTORIGGING_IN_PROGRESS',
      'RETARGETING_IN_PROGRESS',
    ].includes(status);
  };

  return (
    <SubPanel
      id={id}
      elementToTrack={elementToTrack}
      subPanelKey={subPanelKey}
      closePanel={() => uiStore.setSubPanelKey('')}
    >
      <div className="mb-4 flex gap-4">
        <ToggleGroup
          className="gap-4"
          stretch
          onValueChange={uiStore.setSubPanelSectionKey}
          value={subPanelSectionKey}
          items={[
            {
              content: (
                <span className="flex gap-1">
                  Custom
                  <span className="text-gray-45">
                    ({uploadedCharacters.length})
                  </span>
                </span>
              ),
              label: 'Custom characters',
              value: CUSTOM_TAB,
            },
            {
              content: (
                <span className="flex gap-1">
                  Cartwheel
                  <span className="text-gray-45">
                    ({freeCharacters.length})
                  </span>
                </span>
              ),
              label: 'Cartwheel characters',
              value: DEFAULT_TAB,
            },
          ]}
        />
      </div>

      <div className="relative z-0">
        {subPanelSectionKey && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {charactersToDisplay.map((character, index) => (
              <div className="relative" key={index}>
                {(character.uploadStatus === 'NEEDS_VALIDATION' ||
                  character.uploadStatus === 'ADJUSTMENT_FAILED' ||
                  character.uploadStatus === 'FAILED') && (
                  <Tooltip
                    text={
                      character.uploadStatus === 'NEEDS_VALIDATION'
                        ? 'Auto-retargeting failed. Please manually assign bones.'
                        : character.uploadStatus === 'ADJUSTMENT_FAILED'
                          ? 'Adjustment failed. Please verify upload to try again.'
                          : 'The character failed to upload. Delete the character and try again.'
                    }
                    side="top"
                    inModal
                  >
                    <div
                      className="text-red absolute top-2 right-2 z-1 cursor-pointer"
                      onClick={() => {
                        navigationStore.setModal(
                          StudioModalName.UPLOAD_VERIFICATION,
                          {
                            characterID: character.jobID,
                          },
                        );
                      }}
                    >
                      <WarningIcon />
                    </div>
                  </Tooltip>
                )}
                {isProcessing(character.uploadStatus) && (
                  <div className="pointer-events-none absolute inset-0">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="dark:bg-gray-90 flex flex-col items-center gap-2 rounded-lg bg-white p-4">
                        <Loading
                          className="text-green h-8 w-8"
                          trackClassName="stroke-gray-30"
                        />
                        <Text
                          variant="studio/body/medium/sm"
                          className="text-center"
                        >
                          {character.uploadStatus === 'PENDING'
                            ? 'Processing…'
                            : character.uploadStatus ===
                                'AUTORIGGING_IN_PROGRESS'
                              ? 'Auto-rigging…'
                              : 'Retargeting…'}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}
                <AnimationPreview
                  className={`${isProcessing(character.uploadStatus) ? 'opacity-30' : ''}`}
                  key={character.jobID}
                  active={charUploadID === character.jobID}
                  onClick={async () => {
                    if (isProcessing(character.uploadStatus)) {
                      noticesStore.info(
                        'This character is still processing. Please wait a few minutes and then refresh your page.',
                      );
                      return;
                    }
                    // FAILED characters shouldn't be here anyways, but in case they do slip through they can't be set as active either.
                    if (character.uploadStatus === 'FAILED') {
                      noticesStore.error(
                        'The character failed to upload. Delete the character and try again.',
                      );
                      return;
                    }
                    await projectStore.changeCurrentCharacter(character.jobID);
                    const characterType = freeChars(character)
                      ? 'free'
                      : character.jobID.startsWith('char-upload-')
                        ? 'upload'
                        : 'generated';
                    trackCharacterTypeInUse({
                      character_id: character.jobID,
                      character_type: characterType,
                      source: CHARACTER_TYPE_IN_USE_SOURCES.CHARACTER_PANEL,
                    });
                    uiStore.setSubPanelKey('');
                  }}
                  title={character.characterName}
                  subtitle={
                    character.jobCreatedBy !== 'FREE_CHARACTERS'
                      ? formatDistance(character.createdAt, new Date(), {
                          includeSeconds: false,
                          addSuffix: true,
                        })
                      : undefined
                  }
                  mediaPath={character.heroThumbnailPath}
                  mediaType="image"
                  aspect="animation"
                  optionSlot={
                    subPanelSectionKey === CUSTOM_TAB ? (
                      <Dropdown
                        trigger={
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={<ThreeDotsIcon />}
                          />
                        }
                      >
                        <Dropdown.Item
                          onClick={async () => {
                            if (character.createdBy === 'FREE_CHARACTERS') {
                              noticesStore.error(
                                'Cannot rename default characters',
                              );
                              return;
                            }
                            navigationStore.setRenameCharacterModal(
                              character.jobID,
                              character.characterName,
                            );
                          }}
                        >
                          Rename
                        </Dropdown.Item>
                        {!isProcessing(character.uploadStatus) &&
                          character.uploadStatus !== 'FAILED' && (
                            <>
                              <Dropdown.Item
                                onClick={() =>
                                  navigationStore.setModal(
                                    StudioModalName.UPLOAD_VERIFICATION,
                                    {
                                      characterID: character.jobID,
                                    },
                                  )
                                }
                              >
                                Verify upload
                              </Dropdown.Item>
                              {character.markerAdjustmentEligible && (
                                <Dropdown.Item
                                  onClick={() =>
                                    navigationStore.setModal(
                                      StudioModalName.AUTO_RIGGING,
                                      {
                                        characterID: character.jobID,
                                      },
                                    )
                                  }
                                >
                                  Auto-rigging
                                </Dropdown.Item>
                              )}
                              {character.mappingAdjustmentEligible && (
                                <Dropdown.Item
                                  onClick={() =>
                                    navigationStore.setModal(
                                      StudioModalName.RETARGETING,
                                      {
                                        characterID: character.jobID,
                                      },
                                    )
                                  }
                                >
                                  Retargeting
                                </Dropdown.Item>
                              )}
                            </>
                          )}
                        <Dropdown.Item
                          onClick={async () => {
                            if (character.createdBy === 'FREE_CHARACTERS') {
                              noticesStore.error(
                                'Cannot delete default characters',
                              );
                              return;
                            }
                            await characterPickerStore.deleteCustomCharacter(
                              character.jobID,
                            );
                            await characterPickerStore.fetchAvailableCharacters();
                          }}
                        >
                          <span className="text-red">Delete character </span>
                        </Dropdown.Item>
                      </Dropdown>
                    ) : undefined
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </SubPanel>
  );
}
